/**
 * ArrivalsBlock — quién llega hoy y qué falta para que la llegada salga bien.
 *
 * Es de los bloques que sí puede ir detallado: son pocas filas y cada dato
 * cambia una decisión. En móvil son tarjetas; en escritorio, la tabla densa
 * con la convención visual de V2 (columnas con fondo alterno tenue, resalte de
 * fila solo al pasar el ratón).
 *
 * La tabla tiene que caber en su columna sin scroll horizontal, así que en
 * escritorio: nada de `whitespace-nowrap` (el texto se corta o se parte), los
 * botones bajan a talla de ratón y "Abrir reserva" viaja en el nombre del
 * huésped en lugar de gastar una acción entera. Ninguna acción es real.
 */
import React from "react";
import { Warning, Car, ChatCircleText, Check } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { formatEUR } from "@/lib/pmsSelectors";
import { PRE_CHECKIN_LABEL } from "@/data/todayV2Demo";
import {
  TodayBlock, TodayCard, RowList, Row, Meta, StatusPill, QuickAction,
  EmptyState, DenseTable,
} from "@/pages/admin/pms/v2/V2Primitives";

const DONE = new Set(["READY", "INSPECTED"]);

/**
 * El huésped no ha dicho a qué hora llega.
 *
 * Se dice así, con el "—" que V2 usa para lo que falta, y no con la hora de
 * entrada de la casa: esa es el horario de la propiedad, y puesta en la
 * columna "Hora" recepción la lee como "dijo que llegaba a las dos".
 */
function NoEta() {
  return (
    <span
      className="text-stone-300 dark:text-stone-700"
      aria-label="Sin hora prevista"
      title="El huésped no ha dicho a qué hora llega"
    >
      —
    </span>
  );
}

export default function ArrivalsBlock({
  arrivals, housekeeping, desktop, showGuestNames, unitLabel, onCheckIn, onMessage,
}) {
  const notReady = new Set(
    housekeeping.filter((t) => t.advanceable && !DONE.has(t.status)).map((t) => t.unit_id),
  );
  const rows = arrivals.map((a) => ({ ...a, unit_not_ready: notReady.has(a.unit.villa_id) }));

  return (
    <TodayBlock id="today-arrivals" title="Llegadas" count={rows.length} testId="today-arrivals">
      {rows.length === 0 ? (
        <EmptyState testId="today-arrivals-empty">Hoy no llega nadie.</EmptyState>
      ) : desktop ? (
        <ArrivalsTable
          rows={rows}
          showGuestNames={showGuestNames}
          unitLabel={unitLabel}
          onCheckIn={onCheckIn}
          onMessage={onMessage}
        />
      ) : (
        <TodayCard padding="p-0">
          <RowList testId="today-arrivals-list">
            {rows.map((a) => (
              <ArrivalCard
                key={a.reservation.reservation_id}
                arrival={a}
                showGuestNames={showGuestNames}
                unitLabel={unitLabel}
                onCheckIn={onCheckIn}
                onMessage={onMessage}
              />
            ))}
          </RowList>
        </TodayCard>
      )}
    </TodayBlock>
  );
}

/* -------------------------------------------------------------------------
 * Móvil — sin cambios de densidad: aquí manda el dedo.
 * ---------------------------------------------------------------------- */

function ArrivalCard({ arrival, showGuestNames, unitLabel, onCheckIn, onMessage }) {
  const r = arrival.reservation;
  const id = r.reservation_id;
  return (
    <Row testId={`arrival-${id}`}>
      <div className="flex items-start gap-3">
        <div className="w-11 shrink-0 text-sm text-stone-900 dark:text-stone-100 tabular-nums">
          {arrival.eta || <NoEta />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-stone-900 dark:text-stone-100 truncate">
            {showGuestNames ? r.guest.name : `${r.guest.adults + r.guest.children} huéspedes`}
          </div>
          <Meta items={[
            `${arrival.unit.villa_id} · ${arrival.unit.name}`,
            arrival.channel.label,
            arrival.balance_due_eur > 0 ? `saldo ${formatEUR(arrival.balance_due_eur)}` : null,
          ]} />
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            <ArrivalSignals arrival={arrival} unitLabel={unitLabel} />
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            <QuickAction
              label={arrival.checked_in ? "Check-in hecho" : "Check-in"}
              primary={!arrival.checked_in}
              disabled={arrival.checked_in}
              onClick={() => onCheckIn(id)}
              testId={`arrival-checkin-${id}`}
            />
            <QuickAction
              label="Abrir reserva"
              to={`/pms/reservations/${id}`}
              testId={`arrival-open-${id}`}
            />
            {showGuestNames && (
              <QuickAction
                label="Mensaje"
                icon={ChatCircleText}
                onClick={() => onMessage(arrival)}
                testId={`arrival-message-${id}`}
              />
            )}
          </div>
        </div>
      </div>
    </Row>
  );
}

/* -------------------------------------------------------------------------
 * Escritorio
 * ---------------------------------------------------------------------- */

const COLUMNS = [
  { key: "eta", label: "Hora" },
  { key: "guest", label: "Huésped" },
  { key: "unit", label: "Unidad" },
  { key: "channel", label: "Canal" },
  { key: "state", label: "Estado" },
  { key: "balance", label: "Saldo", align: "right" },
  { key: "actions", label: "Check-in", align: "right" },
];

function ArrivalsTable({ rows, showGuestNames, unitLabel, onCheckIn, onMessage }) {
  return (
    <DenseTable
      testId="today-arrivals-table"
      caption="Llegadas de hoy"
      columns={COLUMNS}
      rows={rows.map((a) => ({
        key: a.reservation.reservation_id,
        testId: `arrival-${a.reservation.reservation_id}`,
        arrival: a,
      }))}
      renderRow={({ arrival }) => {
        const r = arrival.reservation;
        const id = r.reservation_id;
        return [
          arrival.eta ? (
            <span className="tabular-nums text-stone-900 dark:text-stone-100 whitespace-nowrap">
              {arrival.eta}
            </span>
          ) : <NoEta />,
          // El nombre ES el enlace a la reserva: misma acción, cero ancho extra.
          <Link
            to={`/pms/reservations/${id}`}
            data-testid={`arrival-open-${id}`}
            title={`Abrir reserva ${id}`}
            className="block min-w-0 max-w-[9rem] truncate text-stone-900 dark:text-stone-100 hover:underline"
          >
            {showGuestNames ? r.guest.name : `${r.guest.adults + r.guest.children} huéspedes`}
          </Link>,
          <span className="block max-w-[6rem] truncate" title={arrival.unit.name}>
            {arrival.unit.villa_id}
            <span className="text-stone-400"> {arrival.unit.name.replace("Villa ", "")}</span>
          </span>,
          <span className="block max-w-[5.5rem] truncate text-stone-500 dark:text-stone-400" title={arrival.channel.label}>
            {arrival.channel.label}
          </span>,
          <div className="flex flex-wrap items-center gap-1">
            <ArrivalSignals arrival={arrival} unitLabel={unitLabel} compact />
          </div>,
          <span className={`tabular-nums whitespace-nowrap ${
            arrival.balance_due_eur > 0
              ? "text-amber-700 dark:text-amber-300"
              : "text-stone-400"
          }`}>
            {arrival.balance_due_eur > 0 ? formatEUR(arrival.balance_due_eur) : "—"}
          </span>,
          <div className="flex justify-end gap-1">
            <QuickAction
              label={arrival.checked_in ? "Hecho" : "Check-in"}
              icon={arrival.checked_in ? Check : undefined}
              primary={!arrival.checked_in}
              disabled={arrival.checked_in}
              size="sm"
              onClick={() => onCheckIn(id)}
              testId={`arrival-checkin-${id}`}
            />
            {showGuestNames && (
              <QuickAction
                label="Mensaje"
                icon={ChatCircleText}
                iconOnly
                size="sm"
                onClick={() => onMessage(arrival)}
                testId={`arrival-message-${id}`}
              />
            )}
          </div>,
        ];
      }}
    />
  );
}

/* -------------------------------------------------------------------------
 * Señales
 * ---------------------------------------------------------------------- */

/**
 * Lo que puede torcer la llegada, en el orden en que preocupa. En escritorio
 * las etiquetas se acortan y el texto completo vive en el `title`: la señal se
 * ve igual y la columna no se desborda.
 */
function ArrivalSignals({ arrival, unitLabel, compact = false }) {
  const readiness = arrival.readiness;
  const out = [];

  if (arrival.unit_not_ready) {
    const full = `${unitLabel.singular} sin terminar`;
    out.push(
      <StatusPill key="unit" tone="red" dot={false} title={full}>
        <span className="inline-flex items-center gap-1">
          <Warning size={10} weight="fill" />
          {compact ? unitLabel.singular : full}
        </span>
      </StatusPill>,
    );
  }
  if (readiness && readiness.pre_checkin !== "completed") {
    const full = `Check-in ${PRE_CHECKIN_LABEL[readiness.pre_checkin].toLowerCase()}`;
    out.push(
      <StatusPill key="checkin" tone="amber" title={full}>
        {compact ? "Check-in" : full}
      </StatusPill>,
    );
  }
  if (arrival.transfer) {
    const pending = !arrival.transfer.confirmed;
    const full = `Traslado ${pending ? "sin confirmar" : "confirmado"}`;
    out.push(
      <StatusPill key="transfer" tone={pending ? "amber" : "emerald"} dot={false} title={full}>
        <span className="inline-flex items-center gap-1">
          <Car size={10} weight="fill" />
          {compact ? "Traslado" : full}
        </span>
      </StatusPill>,
    );
  }
  if (arrival.checked_in) {
    out.push(
      <StatusPill key="in" tone="emerald" title="Check-in hecho">
        {compact ? "Dentro" : "Check-in hecho"}
      </StatusPill>,
    );
  }
  if (out.length === 0) {
    out.push(<StatusPill key="ok" tone="emerald">Todo listo</StatusPill>);
  }
  return out;
}
